import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import FlowDesigner from "@/components/FlowDesigner";
import TestCallModal from "@/components/TestCallModal";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Megaphone, Plus, Minus, Stop, FlowArrow, Phone, Archive, ArrowCounterClockwise,
  Copy, Check, PencilSimple, Play, Trash, Asterisk, ArticleNyTimes, DotsThreeVertical, CaretDown,
  ChartLineUp, HandPointing, Robot, Brain, Plugs, Circle, Pause, WarningCircle,
} from "@phosphor-icons/react";
import AsteriskWizard from "@/components/AsteriskWizard";
import PredictiveAddWizard from "@/components/PredictiveAddWizard";
import ManualAddWizard from "@/components/ManualAddWizard";
import AiAgentAddWizard from "@/components/AiAgentAddWizard";
import RichHtmlEditorDialog from "@/components/RichHtmlEditorDialog";
import CampaignFormFields from "@/components/CampaignFormFields";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import {
  emptyCampaignFormState,
  validateCampaignForm,
  buildCreateCampaignBody,
  dialingTypeLabel,
  DIALING_TYPE_PICKER,
} from "@/lib/campaignForm";
import useCampaignFormOptions from "@/hooks/useCampaignFormOptions";

const DIALING_TYPE_ICONS = {
  predictive: ChartLineUp,
  manual: HandPointing,
  ai_bot: Robot,
  bot: Brain,
};

// Flow-health chip so an incomplete/empty flow is obvious before a campaign
// takes a call. green = ready · amber = incomplete (dead-end/dangling) · grey = no flow yet.
const FLOW_HEALTH = {
  ready: { label: "Flow ready", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200", title: "Flow passes validation — safe to take calls." },
  incomplete: { label: "Flow incomplete", cls: "bg-amber-100 text-amber-700 border border-amber-200", title: "Flow has a dead-end/dangling node and would drop calls. Open Design flow to fix." },
  empty: { label: "No flow", cls: "bg-muted text-muted-foreground border border-border", title: "No flow designed yet. Open Design flow to build one." },
};
function FlowHealthBadge({ health, id }) {
  const h = FLOW_HEALTH[health] || FLOW_HEALTH.empty;
  return (
    <span data-testid={`campaign-flow-health-${id}`} title={h.title}
      className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full ${h.cls}`}>
      {h.label}
    </span>
  );
}

const DROP_RATIO_TOOLTIP =
  "Technical Drop Ratio = (technical drops ÷ answered calls) × 100. " +
  "Counts confirmed platform/media/bridge failures after a live answer. " +
  "Excludes no-answer, busy, invalid number, AMD/voicemail, normal hangups, " +
  "and dialer pacing abandons (queue timeout / no ready agent).";

/** P0-05 — pacing abandon vs compliance abandon (incl AMD hangups). */
function AbandonRatesCell({ camp, testid }) {
  const dialing = String(camp?.dialing_type || "").toLowerCase();
  if (dialing !== "predictive") {
    return <span data-testid={testid} className="text-muted-foreground">—</span>;
  }
  const legacy = camp?.abandon_rate_today || camp?.abandon_rate;
  const incl = camp?.abandon_rate_incl_amd_today || camp?.abandon_rate_incl_amd;
  const legacyDisp = legacy?.display || "—";
  const inclDisp = incl?.display || "—";
  const flag = camp?.pacing_use_abandon_rate_incl_amd ? "pacing:incl_amd" : "pacing:legacy";
  return (
    <span
      data-testid={testid}
      title={`Abandon (pacing) ${legacyDisp}; incl AMD unverified/timeout ${inclDisp}. Switch ${flag} (default off).`}
      className="tabular-nums text-xs leading-tight"
    >
      <span className="block">{legacyDisp}</span>
      <span className="block text-muted-foreground">incl {inclDisp}</span>
    </span>
  );
}

/** PD Technical Drop Ratio — N/A (—) when there are no answered calls. */
function DropRatioCell({ camp, testid }) {
  const dialing = String(camp?.dialing_type || "").toLowerCase();
  const isPd = dialing === "predictive" || dialing === "manual" || dialing === "bot";
  if (!isPd) {
    return (
      <span data-testid={testid} className="text-muted-foreground" title="Drop Ratio applies to Predictive / Manual / Bot campaigns">
        —
      </span>
    );
  }
  const answered = Number(camp?.answered_calls || 0);
  const drops = Number(camp?.technical_drops || 0);
  const undefinedRatio = answered <= 0 || camp?.drop_ratio_undefined === true;
  const display = undefinedRatio
    ? "—"
    : (camp?.drop_ratio_display || (camp?.drop_ratio_pct != null ? `${Number(camp.drop_ratio_pct).toFixed(2)}%` : "—"));
  const detail = answered > 0
    ? `${drops} technical drops ÷ ${answered} answered = ${display}`
    : "No answered calls — ratio undefined (not 0%).";
  const title = `${DROP_RATIO_TOOLTIP} ${detail}`;
  const high = !undefinedRatio && Number(camp?.drop_ratio_pct) >= 5;
  return (
    <span
      data-testid={testid}
      title={title}
      className={`tabular-nums font-semibold ${undefinedRatio ? "text-muted-foreground" : high ? "text-amber-700 dark:text-amber-300" : "text-foreground"}`}
    >
      {display}
      {!undefinedRatio && (
        <span className="block text-[10px] font-normal text-muted-foreground leading-tight">
          {drops}/{answered}
        </span>
      )}
    </span>
  );
}

/** Leads the campaign can still dial: leads in its assigned lists whose dial
 * status is one of the campaign's selected Dial Statuses. 0 means the dialer has
 * nothing to hand to a Ready agent. Click opens the list × status breakdown. */
function DialableLeads({ camp, testid, onClick }) {
  const statuses = camp.dial_statuses || [];
  if (statuses.length === 0) {
    return (
      <span data-testid={testid} className="text-muted-foreground"
            title="No dial statuses selected. VICI Bot campaigns are dialed by VICIdial, not from lead lists.">
        —
      </span>
    );
  }
  const total = camp.dialable_leads ?? 0;
  const breakdown = Object.entries(camp.dialable_by_status || {})
    .map(([s, n]) => `${s} ${n}`).join(" · ");
  const cls = `font-semibold tabular-nums ${total > 0 ? "" : "text-destructive"}`;
  const title = `Dial statuses: ${statuses.join(", ")} — ${breakdown || "no leads left in these statuses"}. Click for list breakdown.`;
  if (onClick) {
    return (
      <button
        type="button"
        data-testid={testid}
        title={title}
        onClick={onClick}
        className={`${cls} hover:underline hover:text-primary cursor-pointer`}
      >
        {total}
      </button>
    );
  }
  return (
    <span data-testid={testid} title={title} className={cls}>
      {total}
    </span>
  );
}

const CALL_LIMIT_ZERO_MSG = "Your call limit is 0. Purchase call capacity on the Call Limit page before starting a campaign.";
const PD_CALL_LIMIT_ZERO_MSG = "Your PD call limit is 0. Purchase PD call capacity on the Call Limit page before starting a campaign.";
const AI_CALL_LIMIT_ZERO_MSG = "Your AI call limit is 0. Purchase AI call capacity on the Call Limit page before starting a campaign.";

function isCallLimitZeroError(detail) {
  const text = typeof detail === "string" ? detail : formatApiError(detail);
  return /call limit is 0|call capacity|call-limit/i.test(text || "");
}

function usesPdCallLimit(camp) {
  const d = String(camp?.dialing_type || "").toLowerCase();
  return d === "predictive" || d === "manual";
}

/** Start/stop status control — same footprint as the Actions button. */
function CampaignStatusButton({ camp, busy, onToggle }) {
  const st = String(camp?.status || "draft").toLowerCase();
  const archived = Boolean(camp?.archived_at);
  const styles = {
    active: {
      // Darken green only; keep white text (override outline hover:text-accent).
      cls: "bg-emerald-600 text-white border-transparent hover:bg-emerald-700 hover:!text-white",
      Icon: Pause,
      title: "Stop campaign",
    },
    stopped: {
      cls: "bg-rose-50 text-rose-700 border-rose-200/80 hover:bg-rose-100 hover:!text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800/60 dark:hover:bg-rose-950/60 dark:hover:!text-rose-300",
      Icon: Circle,
      title: "Start campaign",
    },
    draft: {
      cls: "bg-slate-100 text-slate-700 border-slate-200/90 hover:bg-slate-200/80 hover:!text-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700 dark:hover:!text-slate-200",
      Icon: Circle,
      title: "Start campaign",
    },
  };
  const conf = styles[st] || styles.draft;
  const Icon = conf.Icon;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={`rounded-lg h-8 gap-1 px-2.5 ${conf.cls}`}
      disabled={busy === camp.id || archived}
      onClick={() => onToggle(camp)}
      data-testid={`campaign-status-btn-${camp.id}`}
      title={conf.title}
    >
      <Icon className="w-3.5 h-3.5" weight="fill" />
      <span className="capitalize">{camp.status || "draft"}</span>
    </Button>
  );
}

/** Amber warning next to campaign name when required infra is missing/offline. */
function CampaignInfraAlert({ camp }) {
  const alert = camp?.infra_alert;
  if (!alert?.alert) return null;
  const summary = camp.infra_alert_summary
    || (alert.issues || []).map((it) => {
      if (it.reason === "missing") return `${it.label}: not assigned`;
      if (it.reason === "all_offline") return `${it.label}: all offline`;
      if (it.reason === "offline") return `${it.label}: offline`;
      return `${it.label}: ${it.reason}`;
    }).join("; ");
  return (
    <span
      className="inline-flex shrink-0 text-amber-600 dark:text-amber-400"
      title={summary}
      data-testid={`campaign-infra-alert-${camp.id}`}
      aria-label={summary}
    >
      <WarningCircle className="w-4 h-4" weight="fill" />
    </span>
  );
}

/** Actions available per dialing type (product campaign-view spec). */
function campaignActionKeys(dialingType) {
  const d = String(dialingType || "ai_bot").toLowerCase();
  if (d === "predictive" || d === "manual") {
    return new Set(["start", "stop", "edit", "asterisk", "script", "archive", "restore", "delete"]);
  }
  if (d === "bot") {
    // AI Agent — no Asterisk; includes Web Hooks API
    return new Set(["start", "stop", "edit", "design", "test", "webhooks", "archive", "restore", "delete"]);
  }
  // VICI Bot (ai_bot)
  return new Set(["start", "stop", "edit", "design", "test", "asterisk", "archive", "restore", "delete"]);
}

/** Single Actions control: hover opens a labeled menu filtered by dialing type. */
function CampaignRowActions({
  camp,
  busy,
  onStart,
  onStop,
  onEdit,
  onDesign,
  onTestCall,
  onAsterisk,
  onAgentScript,
  onWebhooks,
  onArchive,
  onRestore,
  onDelete,
}) {
  const archived = Boolean(camp.archived_at);
  const isActive = camp.status === "active";
  const canStart = !isActive && !archived;
  const allowed = campaignActionKeys(camp.dialing_type);
  const items = [
    canStart && {
      key: "start",
      label: "Start campaign",
      icon: Play,
      iconWeight: "fill",
      className: "text-primary",
      disabled: busy === camp.id,
      onSelect: () => onStart(camp),
      testid: `start-campaign-${camp.id}`,
    },
    isActive && {
      key: "stop",
      label: "Stop campaign",
      icon: Stop,
      iconWeight: "fill",
      className: "text-destructive",
      disabled: busy === camp.id,
      onSelect: () => onStop(camp),
      testid: `stop-campaign-${camp.id}`,
    },
    {
      key: "edit",
      label: "Edit campaign",
      icon: PencilSimple,
      onSelect: () => onEdit(camp),
      testid: `edit-campaign-${camp.id}`,
    },
    {
      key: "design",
      label: "Design flow",
      icon: FlowArrow,
      onSelect: () => onDesign(camp),
      testid: `design-campaign-${camp.id}`,
    },
    {
      key: "test",
      label: "Test call",
      icon: Phone,
      iconWeight: "fill",
      onSelect: () => onTestCall(camp),
      testid: `test-call-${camp.id}`,
    },
    {
      key: "asterisk",
      label: "Asterisk server setup",
      icon: Asterisk,
      onSelect: () => onAsterisk(camp),
      testid: `asterisk-server-${camp.id}`,
    },
    {
      key: "script",
      label: "Agent script",
      icon: ArticleNyTimes,
      onSelect: () => onAgentScript(camp),
      testid: `agent-script-${camp.id}`,
    },
    {
      key: "webhooks",
      label: "Web Hooks API",
      icon: Plugs,
      onSelect: () => onWebhooks?.(camp),
      testid: `webhooks-campaign-${camp.id}`,
    },
    archived
      ? {
        key: "restore",
        label: "Restore",
        icon: ArrowCounterClockwise,
        disabled: busy === camp.id,
        onSelect: () => onRestore(camp),
        testid: `restore-campaign-${camp.id}`,
      }
      : {
        key: "archive",
        label: "Archive",
        icon: Archive,
        disabled: busy === camp.id,
        onSelect: () => onArchive(camp),
        testid: `archive-campaign-${camp.id}`,
      },
    {
      key: "delete",
      label: "Delete campaign",
      icon: Trash,
      className: "text-destructive focus:text-destructive",
      disabled: busy === camp.id,
      onSelect: () => onDelete(camp),
      testid: `delete-campaign-${camp.id}`,
    },
  ].filter((item) => item && allowed.has(item.key));

  return (
    <HoverCard openDelay={80} closeDelay={120}>
      <HoverCardTrigger asChild>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-lg h-8 gap-1 px-2.5"
          data-testid={`campaign-actions-${camp.id}`}
          aria-label="Campaign actions"
        >
          <DotsThreeVertical className="w-4 h-4" weight="bold" />
          Actions
          <CaretDown className="w-3 h-3 opacity-70" />
        </Button>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="w-56 p-1"
        data-testid={`campaign-actions-menu-${camp.id}`}
      >
        <div className="flex flex-col" role="menu">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                data-testid={item.testid}
                title={item.label}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!item.disabled) item.onSelect();
                }}
                className={
                  "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm outline-none "
                  + "hover:bg-muted focus:bg-muted disabled:pointer-events-none disabled:opacity-40 "
                  + (item.className || "")
                }
              >
                <Icon className="w-4 h-4 shrink-0" weight={item.iconWeight || "regular"} />
                <span className="truncate">{item.label}</span>
              </button>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

/** Prefix chip with an in-badge copy control (right after the digits). */
function PrefixCopyChip({ prefix, campId, copied, onCopy }) {
  if (!prefix) return null;
  return (
    <span
      className="inline-flex items-center gap-1 font-mono px-2 py-0.5 rounded bg-muted"
      data-testid={`campaign-prefix-${campId}`}
    >
      <span>Prefix {prefix}</span>
      <button
        type="button"
        className="inline-flex items-center justify-center h-5 w-5 rounded hover:bg-background/80 text-foreground/80 hover:text-foreground shrink-0"
        onClick={(e) => { e.stopPropagation(); onCopy(prefix, campId); }}
        data-testid={`copy-prefix-${campId}`}
        title="Copy prefix"
        aria-label="Copy prefix"
      >
        {copied === campId
          ? <Check className="w-3.5 h-3.5 text-emerald-600" weight="bold" />
          : <Copy className="w-3.5 h-3.5" weight="bold" />}
      </button>
    </span>
  );
}

export default function Campaigns() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isCustomer = user?.role === "customer";
  const [campaigns, setCampaigns] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [predictiveOpen, setPredictiveOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [aiAgentOpen, setAiAgentOpen] = useState(false);
  const [pickDialingOpen, setPickDialingOpen] = useState(false);
  const [createForm, setCreateForm] = useState(() => emptyCampaignFormState());
  const {
    options: formOptions, optionsError, pdServers, vicidial,
    defaultOpensipsId, defaultMediaId, defaultVicidialId, reload: reloadFormOptions,
  } = useCampaignFormOptions(isCustomer);
  const [noVicidialOpen, setNoVicidialOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [designer, setDesigner] = useState(null);
  const [savingFlow, setSavingFlow] = useState(false);
  const [testCall, setTestCall] = useState(null);
  const [createVmUploading, setCreateVmUploading] = useState(false);
  const createVmInputRef = useRef(null);
  const patchCreate = (patch) => setCreateForm((prev) => ({ ...prev, ...patch }));
  const [confirm, setConfirm] = useState(null); // { action: "copy"|"delete", camp }
  const [asterisk, setAsterisk] = useState(null); // campaign for the Asterisk wizard
  const [scriptCamp, setScriptCamp] = useState(null); // campaign for agent script editor
  const [savingScript, setSavingScript] = useState(false);
  const [copiedPrefix, setCopiedPrefix] = useState("");
  const [callLimitModal, setCallLimitModal] = useState(null); // error message string | null
  const [dialLevelCamp, setDialLevelCamp] = useState(null); // campaign for dial-level modal
  const [dialLevelValue, setDialLevelValue] = useState("1");
  const [savingDialLevel, setSavingDialLevel] = useState(false);
  const [webhooksCamp, setWebhooksCamp] = useState(null);

  const createFormDefaults = useCallback((dialingType) => {
    const opsId = defaultOpensipsId || "";
    const medId = defaultMediaId || "";
    return emptyCampaignFormState({
      dialingType: dialingType || "predictive",
      opensipsId: opsId || "__none__",
      opensipsIds: opsId ? [opsId] : [],
      mediaServerId: medId,
      mediaServerIds: medId ? [medId] : [],
      vicidialId: defaultVicidialId || "",
      pdServerId: pdServers.find((s) => s.is_default)?.id || pdServers[0]?.id || "",
      pdServerIds: [pdServers.find((s) => s.is_default)?.id || pdServers[0]?.id].filter(Boolean),
      // Predictive Dial Level (lines/agent); AI Agent Dial Rate (CPS).
      ...(dialingType === "predictive" ? { dialLevel: "1" } : {}),
      ...(dialingType === "bot" ? { dialRate: "1" } : {}),
    });
  }, [defaultOpensipsId, defaultMediaId, defaultVicidialId, pdServers]);

  const openCreateForDialingType = (dialingType) => {
    if (dialingType === "ai_bot") {
      const activeVicidial = (vicidial || []).filter(
        (s) => String(s.status || "active").toLowerCase() === "active",
      );
      if (!activeVicidial.length) {
        setPickDialingOpen(false);
        setNoVicidialOpen(true);
        return;
      }
    }
    setCreateForm(createFormDefaults(dialingType));
    setPickDialingOpen(false);
    if (dialingType === "predictive") {
      setPredictiveOpen(true);
      setManualOpen(false);
      setAiAgentOpen(false);
      setOpen(false);
    } else if (dialingType === "manual") {
      setManualOpen(true);
      setPredictiveOpen(false);
      setAiAgentOpen(false);
      setOpen(false);
    } else if (dialingType === "bot") {
      setAiAgentOpen(true);
      setPredictiveOpen(false);
      setManualOpen(false);
      setOpen(false);
    } else {
      setPredictiveOpen(false);
      setManualOpen(false);
      setAiAgentOpen(false);
      setOpen(true);
    }
  };

  const closeCreateDialogs = () => {
    setOpen(false);
    setPredictiveOpen(false);
    setManualOpen(false);
    setAiAgentOpen(false);
  };

  const backToDialingPicker = () => {
    closeCreateDialogs();
    setPickDialingOpen(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns", { params: { include_archived: showArchived } });
      setCampaigns(data);
    } finally { setLoading(false); }
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);
  // Keep the live "Active Calls" column fresh (silent refresh, no spinner flicker).
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/campaigns", { params: { include_archived: showArchived } });
        setCampaigns(data);
      } catch { /* ignore transient polling errors */ }
    }, 10000);
    return () => clearInterval(t);
  }, [showArchived]);
  const create = async () => {
    const check = validateCampaignForm(createForm, { pdServerCount: pdServers.length });
    if (!check.ok) { toast.error(check.error); return; }
    setSaving(true);
    try {
      const body = buildCreateCampaignBody(createForm);
      await api.post("/campaigns", body);
      toast.success("Campaign created");
      closeCreateDialogs();
      setCreateForm(emptyCampaignFormState());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const stop = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/concurrency`, { concurrency: 0 }); toast.success("Campaign stopped & holds released"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const archive = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/archive`); toast.success("Campaign archived"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const restore = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/restore`); toast.success("Campaign restored"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const copyPrefix = async (prefix, campId) => {
    if (!prefix) return;
    try {
      await navigator.clipboard.writeText(String(prefix));
      setCopiedPrefix(campId);
      toast.success("Prefix copied");
      setTimeout(() => setCopiedPrefix((c) => (c === campId ? "" : c)), 2000);
    } catch {
      toast.error("Copy failed — select the prefix and copy manually.");
    }
  };

  const openCallLimitModal = (message) => {
    setCallLimitModal(message || CALL_LIMIT_ZERO_MSG);
  };

  const start = async (camp) => {
    setBusy(camp.id);
    try {
      if (isCustomer) {
        const { data: lim } = await api.get("/billing/call-limit");
        const pdOnly = usesPdCallLimit(camp);
        const limit = pdOnly
          ? lim?.pd_call_limit
          : (lim?.ai_call_limit ?? lim?.concurrent_call_limit);
        if (limit == null || Number(limit) <= 0) {
          openCallLimitModal(pdOnly ? PD_CALL_LIMIT_ZERO_MSG : AI_CALL_LIMIT_ZERO_MSG);
          return;
        }
      }
      await api.post(`/campaigns/${camp.id}/concurrency`, { concurrency: Math.max(1, camp.concurrency || 1) });
      toast.success("Campaign started");
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (isCallLimitZeroError(detail)) {
        openCallLimitModal(
          formatApiError(detail)
          || (usesPdCallLimit(camp) ? PD_CALL_LIMIT_ZERO_MSG : AI_CALL_LIMIT_ZERO_MSG),
        );
      } else {
        // Surface balance / validation errors (e.g. $2 AI campaign minimum) clearly.
        toast.error(formatApiError(detail) || "Could not start campaign");
      }
      load();
    } finally { setBusy(null); }
  };

  const toggleStatus = async (camp) => {
    if (camp.archived_at) return;
    if (camp.status === "active") await stop(camp);
    else await start(camp);
  };

  const openDialLevel = (camp) => {
    const d = String(camp.dialing_type || "").toLowerCase();
    if (d !== "predictive" && d !== "bot") return;
    if (d === "bot") {
      const rate = camp.dial_rate != null && camp.dial_rate !== ""
        ? camp.dial_rate
        : camp.dial_level; // legacy CPS stored as dial_level
      setDialLevelValue(rate != null && rate !== "" ? String(rate) : "1");
    } else {
      setDialLevelValue(
        camp.dial_level != null && camp.dial_level !== ""
          ? String(camp.dial_level)
          : "1",
      );
    }
    setDialLevelCamp(camp);
  };

  const saveDialLevel = async () => {
    if (!dialLevelCamp) return;
    const n = Number(dialLevelValue);
    const isBot = String(dialLevelCamp.dialing_type || "").toLowerCase() === "bot";
    const label = isBot ? "Dial Rate" : "Dial Level";
    if (!Number.isFinite(n) || n <= 0) {
      toast.error(`${label} must be a positive number`);
      return;
    }
    setSavingDialLevel(true);
    try {
      await api.put(
        `/campaigns/${dialLevelCamp.id}`,
        isBot ? { dial_rate: n } : { dial_level: n },
      );
      toast.success(`${label} updated`);
      setDialLevelCamp(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || `Could not update ${label}`);
    } finally {
      setSavingDialLevel(false);
    }
  };
  const duplicate = async (camp) => {
    setBusy(camp.id);
    try { await api.post(`/campaigns/${camp.id}/duplicate`); toast.success(`Copied "${camp.name}"`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const del = async (camp) => {
    setBusy(camp.id);
    try { await api.delete(`/campaigns/${camp.id}`); toast.success(`Deleted "${camp.name}"`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const openAgentScript = async (camp) => {
    setScriptCamp(camp);
    try {
      const { data } = await api.get(`/campaigns/${camp.id}`);
      if (data?.id) setScriptCamp((prev) => (prev?.id === camp.id ? { ...prev, ...data } : prev));
    } catch {
      /* list row fields are enough to edit */
    }
  };
  const saveAgentScript = async (html) => {
    if (!scriptCamp) return;
    setSavingScript(true);
    try {
      await api.put(`/campaigns/${scriptCamp.id}`, { agent_script: html || "" });
      toast.success("Agent script saved");
      setScriptCamp(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save script");
    } finally {
      setSavingScript(false);
    }
  };
  const runConfirm = async () => {
    const c = confirm; setConfirm(null);
    if (!c) return;
    if (c.action === "copy") await duplicate(c.camp);
    else if (c.action === "delete") await del(c.camp);
    else if (c.action === "archive") await archive(c.camp);
  };
  const uploadVoicemailCreate = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCreateVmUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCreateForm((a) => ({ ...a, vmAudioId: data.id, vmFilename: data.original_filename }));
      if (data.warning) toast.warning(data.warning);
      else toast.success("Voicemail audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCreateVmUploading(false); e.target.value = ""; }
  };

  const openDesigner = async (camp) => {
    const { data } = await api.get(`/campaigns/${camp.id}`);
    setDesigner({ campaign: data, flow: data.flow || { nodes: [], edges: [] } });
  };
  const saveFlow = async (flow, opts = {}) => {
    setSavingFlow(true);
    try {
      await api.put(`/campaigns/${designer.campaign.id}/flow`, { flow, draft: !!opts.draft });
      toast.success(opts.keepOpen ? "Draft saved — keep editing" : "Flow saved");
      if (opts.keepOpen) { setDesigner((d) => (d ? { ...d, flow } : d)); }
      else { setDesigner(null); load(); }
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingFlow(false); }
  };

  const title = user?.role === "admin" ? "Global Campaigns" : "Campaigns";

  return (
    <div>
      <PageHeader title={title} subtitle="Design a flow, then start the campaign. Concurrent-call limits are set per account.">
        <Button variant={showArchived ? "secondary" : "outline"} className="rounded-lg" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn">
          <Archive className="w-4 h-4 mr-1.5" weight="bold" /> {showArchived ? "Hide archived" : "Show archived"}
        </Button>
        {isCustomer && (
          <>
            <Button
              data-testid="create-campaign-btn"
              className="rounded-lg"
              onClick={() => setPickDialingOpen(true)}
            >
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> New Campaign
            </Button>

            <Dialog open={pickDialingOpen} onOpenChange={setPickDialingOpen}>
              <DialogContent className="max-w-3xl" data-testid="pick-dialing-type-dialog">
                <DialogHeader>
                  <DialogTitle>New campaign</DialogTitle>
                  <DialogDescription>
                    Choose how this campaign will dial. You can finish the rest of the setup on the next step.
                  </DialogDescription>
                </DialogHeader>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2" data-testid="dialing-type-cards">
                  {DIALING_TYPE_PICKER.map((opt) => {
                    const Icon = DIALING_TYPE_ICONS[opt.value] || Megaphone;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-testid={`dialing-type-card-${opt.value}`}
                        onClick={() => openCreateForDialingType(opt.value)}
                        className={
                          "group text-left rounded-xl border border-border bg-card p-5 transition-colors "
                          + "hover:border-primary/40 hover:bg-muted/40 focus-visible:outline-none "
                          + "focus-visible:ring-2 focus-visible:ring-ring"
                        }
                      >
                        <div className="flex items-start gap-4">
                          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-muted text-foreground group-hover:bg-primary/10 group-hover:text-primary">
                            <Icon className="w-8 h-8" weight="duotone" />
                          </span>
                          <div className="min-w-0">
                            <p className="text-base font-semibold tracking-tight">{opt.label}</p>
                            <p className="mt-1.5 text-sm text-muted-foreground leading-snug">
                              {opt.blurb}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>

            <PredictiveAddWizard
              open={predictiveOpen}
              onOpenChange={(next) => {
                setPredictiveOpen(next);
                if (!next) setCreateForm(emptyCampaignFormState());
              }}
              value={createForm}
              onChange={patchCreate}
              options={formOptions}
              optionsError={optionsError}
              onInboundGroupsReload={reloadFormOptions}
              saving={saving}
              onSubmit={create}
              onBackToDialing={backToDialingPicker}
              vmUploading={createVmUploading}
              vmInputRef={createVmInputRef}
              onVmUploadClick={() => createVmInputRef.current?.click()}
              onVmFileChange={uploadVoicemailCreate}
            />

            <ManualAddWizard
              open={manualOpen}
              onOpenChange={(next) => {
                setManualOpen(next);
                if (!next) setCreateForm(emptyCampaignFormState());
              }}
              value={createForm}
              onChange={patchCreate}
              options={formOptions}
              optionsError={optionsError}
              onInboundGroupsReload={reloadFormOptions}
              saving={saving}
              onSubmit={create}
              onBackToDialing={backToDialingPicker}
              vmUploading={createVmUploading}
              vmInputRef={createVmInputRef}
              onVmUploadClick={() => createVmInputRef.current?.click()}
              onVmFileChange={uploadVoicemailCreate}
            />

            <AiAgentAddWizard
              open={aiAgentOpen}
              onOpenChange={(next) => {
                setAiAgentOpen(next);
                if (!next) setCreateForm(emptyCampaignFormState());
              }}
              value={createForm}
              onChange={patchCreate}
              options={formOptions}
              optionsError={optionsError}
              onInboundGroupsReload={reloadFormOptions}
              saving={saving}
              onSubmit={create}
              onBackToDialing={backToDialingPicker}
              vmUploading={createVmUploading}
              vmInputRef={createVmInputRef}
              onVmUploadClick={() => createVmInputRef.current?.click()}
              onVmFileChange={uploadVoicemailCreate}
            />

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="create-campaign-dialog">
                <DialogHeader className="shrink-0">
                  <DialogTitle>Create campaign</DialogTitle>
                  <DialogDescription>
                    Dialing type: <span className="font-medium text-foreground">{dialingTypeLabel(createForm.dialingType)}</span>
                    {" · "}Configure the remaining campaign settings below.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2 overflow-y-auto flex-1 pr-1 -mr-1">
                  <CampaignFormFields
                    mode="create"
                    value={createForm}
                    onChange={patchCreate}
                    options={formOptions}
                    optionsError={optionsError}
                    vmUploading={createVmUploading}
                    vmInputRef={createVmInputRef}
                    onVmUploadClick={() => createVmInputRef.current?.click()}
                    onVmFileChange={uploadVoicemailCreate}
                    onInboundGroupsReload={reloadFormOptions}
                    hideDialingType
                  />
                </div>
                <DialogFooter className="shrink-0 border-t pt-3 gap-2 sm:gap-0">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-lg"
                    onClick={backToDialingPicker}
                    data-testid="create-campaign-back-dialing"
                  >
                    Change dialing type
                  </Button>
                  <Button onClick={create} disabled={saving} data-testid="submit-campaign-btn" className="rounded-lg">
                    {saving ? "Creating…" : "Create"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      </PageHeader>

      {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
        : campaigns.length === 0 ? (
          <EmptyState testid="campaigns-empty" icon={Megaphone} title="No campaigns yet"
            description={isCustomer ? "Create a campaign, design its flow, then start it." : "Campaigns created by customers appear here."} />
        ) : isCustomer ? (
          <div className="rounded-xl border border-border bg-card card-shadow overflow-x-auto" data-testid="campaigns-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Campaign name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-center">Active Calls</TableHead>
                <TableHead className="text-center">Dial level</TableHead>
                <TableHead className="text-center">Leads to Dial</TableHead>
                <TableHead className="text-center" title={DROP_RATIO_TOOLTIP}>Drop Ratio</TableHead>
                <TableHead className="text-center" title="Pacing abandon vs abandon incl AMD unverified/timeout hangups (P0-05)">Abandon</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {campaigns.map((c) => {
                  const dialing = String(c.dialing_type || "ai_bot").toLowerCase();
                  const isViciBot = dialing === "ai_bot";
                  const canEditDialLevel = dialing === "predictive" || dialing === "bot";
                  return (
                  <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <button
                          type="button"
                          className="font-medium text-left text-foreground hover:text-primary hover:underline truncate"
                          onClick={() => navigate(`/campaigns/${c.id}/edit`)}
                          data-testid={`campaign-name-${c.id}`}
                          title="Edit campaign"
                        >
                          {c.name}
                        </button>
                        <CampaignInfraAlert camp={c} />
                      </div>
                      {isViciBot && c.campaign_prefix && (
                        <div className="mt-1">
                          <PrefixCopyChip
                            prefix={c.campaign_prefix}
                            campId={c.id}
                            copied={copiedPrefix}
                            onCopy={copyPrefix}
                          />
                        </div>
                      )}
                      {(c.source === "global" || c.archived_at) && (
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {c.source === "global" && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-accent text-primary">Template</span>}
                          {c.archived_at && <span data-testid={`campaign-archived-badge-${c.id}`} className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">Archived</span>}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className="text-sm text-muted-foreground"
                        data-testid={`campaign-dialing-${c.id}`}
                      >
                        {dialingTypeLabel(c.dialing_type)}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span data-testid={`campaign-active-calls-${c.id}`}
                            title="Live connected calls"
                            className={`inline-flex items-center gap-1.5 font-semibold tabular-nums ${c.active_calls > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                        {c.active_calls > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                        {c.active_calls ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      {canEditDialLevel ? (
                        <button
                          type="button"
                          className="font-semibold tabular-nums text-primary hover:underline"
                          onClick={() => openDialLevel(c)}
                          data-testid={`campaign-dial-level-${c.id}`}
                          title={dialing === "bot" ? "Change Dial Rate (CPS)" : "Change Dial Level"}
                        >
                          {dialing === "bot"
                            ? (c.dial_rate != null && c.dial_rate !== ""
                              ? c.dial_rate
                              : (c.dial_level != null && c.dial_level !== "" ? c.dial_level : "—"))
                            : (c.dial_level != null && c.dial_level !== "" ? c.dial_level : "—")}
                        </button>
                      ) : (
                        <span className="text-muted-foreground tabular-nums" data-testid={`campaign-dial-level-${c.id}`}>—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <DialableLeads
                        camp={c}
                        testid={`campaign-dialable-leads-${c.id}`}
                        onClick={() => navigate(`/campaigns/${c.id}/leads-to-dial`)}
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      <DropRatioCell camp={c} testid={`campaign-drop-ratio-${c.id}`} />
                    </TableCell>
                    <TableCell className="text-center">
                      <AbandonRatesCell camp={c} testid={`campaign-abandon-rates-${c.id}`} />
                    </TableCell>
                    <TableCell>
                      <CampaignStatusButton camp={c} busy={busy} onToggle={toggleStatus} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end">
                        <CampaignRowActions
                          camp={c}
                          busy={busy}
                          onStart={start}
                          onStop={stop}
                          onEdit={(camp) => navigate(`/campaigns/${camp.id}/edit`)}
                          onDesign={openDesigner}
                          onTestCall={setTestCall}
                          onAsterisk={setAsterisk}
                          onAgentScript={openAgentScript}
                          onWebhooks={setWebhooksCamp}
                          onArchive={(camp) => setConfirm({ action: "archive", camp })}
                          onRestore={restore}
                          onDelete={(camp) => setConfirm({ action: "delete", camp })}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="campaigns-grid">
            {campaigns.map((c) => (
              <div key={c.id} className="rounded-xl border border-border bg-card card-shadow p-6" data-testid={`campaign-card-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-1.5 min-w-0">
                      <h3 className="font-semibold text-lg truncate">{c.name}</h3>
                      <CampaignInfraAlert camp={c} />
                    </div>
                    {c.source === "global" && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent text-primary mt-1 inline-block">From template</span>}
                    {!isCustomer && <p className="text-xs text-muted-foreground mt-0.5">{c.customer_name}{c.reseller_name ? ` · ${c.reseller_name}` : " · Direct"}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <FlowHealthBadge health={c.flow_health} id={c.id} />
                    {c.archived_at && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full bg-muted text-muted-foreground" data-testid={`campaign-archived-badge-${c.id}`}>Archived</span>}
                    <StatusPill status={c.status} />
                  </div>
                </div>

                {c.campaign_prefix && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid={`campaign-routing-${c.id}`}>
                    <PrefixCopyChip
                      prefix={c.campaign_prefix}
                      campId={c.id}
                      copied={copiedPrefix}
                      onCopy={copyPrefix}
                    />
                    {c.vicidial_server_name && <span className="text-muted-foreground">{c.vicidial_server_name}{c.vicidial_server_host ? ` · ${c.vicidial_server_host}` : ""}</span>}
                  </div>
                )}

                <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <span className="text-sm text-muted-foreground">Leads to dial</span>
                  <span className="text-sm">
                    <DialableLeads camp={c} testid={`campaign-dialable-leads-card-${c.id}`} />
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <span className="text-sm text-muted-foreground">Active calls</span>
                  <span data-testid={`campaign-active-calls-mobile-${c.id}`}
                        title="Live connected calls (across all AI engine servers)"
                        className={`inline-flex items-center gap-1.5 font-semibold tabular-nums text-sm ${c.active_calls > 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                    {c.active_calls > 0 && <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />}
                    {c.active_calls ?? 0}
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <span className="text-sm text-muted-foreground" title={DROP_RATIO_TOOLTIP}>Drop Ratio</span>
                  <DropRatioCell camp={c} testid={`campaign-drop-ratio-card-${c.id}`} />
                </div>
                <div className="mt-2 flex items-center justify-between rounded-lg bg-muted/40 p-3">
                  <span className="text-sm text-muted-foreground">Abandon (legacy / incl AMD)</span>
                  <AbandonRatesCell camp={c} testid={`campaign-abandon-rates-card-${c.id}`} />
                </div>

                {isCustomer ? (
                  <>
                    <div className="mt-5 flex flex-wrap gap-1.5">
                      {c.status !== "active" && !c.archived_at && (
                        <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0 text-primary" disabled={busy === c.id} onClick={() => start(c)} data-testid={`start-campaign-${c.id}`} title="Start campaign" aria-label="Start campaign"><Play className="w-4 h-4" weight="fill" /></Button>
                      )}
                      <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => openDesigner(c)} data-testid={`design-campaign-${c.id}`} title="Design flow" aria-label="Design flow"><FlowArrow className="w-4 h-4" weight="bold" /></Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => setTestCall(c)} data-testid={`test-call-${c.id}`} title="Test call" aria-label="Test call"><Phone className="w-4 h-4" weight="fill" /></Button>
                      <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" onClick={() => openAgentScript(c)} data-testid={`agent-script-${c.id}`} title="Agent script" aria-label="Agent script"><ArticleNyTimes className="w-4 h-4" weight="bold" /></Button>
                      {c.status === "active" && !c.archived_at && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive hover:text-destructive" disabled={busy === c.id} onClick={() => stop(c)} data-testid={`stop-campaign-${c.id}`} title="Stop campaign" aria-label="Stop campaign"><Stop className="w-4 h-4" weight="fill" /></Button>
                      )}
                      {c.archived_at ? (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0" disabled={busy === c.id} onClick={() => restore(c)} data-testid={`restore-campaign-${c.id}`} title="Restore" aria-label="Restore"><ArrowCounterClockwise className="w-4 h-4" weight="bold" /></Button>
                      ) : (
                        <Button size="sm" variant="outline" className="rounded-lg h-8 w-8 p-0" disabled={busy === c.id} onClick={() => setConfirm({ action: "archive", camp: c })} data-testid={`archive-campaign-${c.id}`} title="Archive" aria-label="Archive"><Archive className="w-4 h-4" weight="bold" /></Button>
                      )}
                    </div>
                  </>
                ) : null}

                <div className={`mt-4 grid ${c.reseller_id ? "grid-cols-2" : "grid-cols-1"} gap-3 text-sm`}>
                  <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">{c.reseller_id ? "Platform rate" : "Effective rate"}</p><p className="font-medium mt-0.5">{c.platform_rate_snapshot_minor != null ? money(c.platform_rate_snapshot_minor) : "—"}</p></div>
                  {c.reseller_id && <div className="rounded-lg border border-border p-3"><p className="text-xs text-muted-foreground">Reseller rate</p><p className="font-medium mt-0.5">{c.reseller_rate_snapshot_minor != null ? money(c.reseller_rate_snapshot_minor) : "—"}</p></div>}
                </div>
              </div>
            ))}
          </div>
        )}

      {/* Flow designer for a customer's own campaign copy */}
      <Dialog open={!!designer} onOpenChange={(v) => !v && setDesigner(null)}>
        <DialogContent className="max-w-[95vw] w-[95vw]" data-testid="campaign-designer-dialog">
          <DialogHeader><DialogTitle>Design flow — {designer?.campaign?.name}{designer?.campaign?.campaign_prefix ? ` · Prefix ${designer.campaign.campaign_prefix}` : ""}</DialogTitle>
            <DialogDescription>Customize your own campaign flow. The original global template is never changed.</DialogDescription></DialogHeader>
          {designer && <FlowDesigner initial={designer.flow} onSave={saveFlow} saving={savingFlow} />}
        </DialogContent>
      </Dialog>

      {testCall && (
        <TestCallModal campaign={testCall} open={!!testCall} onClose={() => setTestCall(null)} />
      )}

      <Dialog open={!!dialLevelCamp} onOpenChange={(open) => !open && setDialLevelCamp(null)}>
        <DialogContent className="max-w-sm" data-testid="dial-level-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialLevelCamp && String(dialLevelCamp.dialing_type || "").toLowerCase() === "bot"
                ? "Dial Rate"
                : "Dial Level"}
            </DialogTitle>
            <DialogDescription>
              {dialLevelCamp
                ? (String(dialLevelCamp.dialing_type || "").toLowerCase() === "bot"
                  ? `Calls per second for “${dialLevelCamp.name}”.`
                  : `Lines per ready agent for “${dialLevelCamp.name}”.`)
                : "Change pacing"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="campaign-dial-level-input">
              {dialLevelCamp && String(dialLevelCamp.dialing_type || "").toLowerCase() === "bot"
                ? "Dial Rate (CPS)"
                : "Dial Level"}
            </Label>
            <Input
              id="campaign-dial-level-input"
              data-testid="dial-level-input"
              type="number"
              min="0.1"
              step="0.1"
              value={dialLevelValue}
              onChange={(e) => setDialLevelValue(e.target.value)}
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setDialLevelCamp(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-lg"
              disabled={savingDialLevel}
              onClick={saveDialLevel}
              data-testid="dial-level-save"
            >
              {savingDialLevel ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!webhooksCamp} onOpenChange={(open) => !open && setWebhooksCamp(null)}>
        <DialogContent className="max-w-lg" data-testid="webhooks-api-dialog">
          <DialogHeader>
            <DialogTitle>Web Hooks API</DialogTitle>
            <DialogDescription>
              Hit this AI Agent campaign to place a call even outside dialing times (campaign must be active).
            </DialogDescription>
          </DialogHeader>
          {webhooksCamp && (
            <div className="space-y-3 text-sm py-1">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Campaign</p>
                <p className="font-medium">{webhooksCamp.name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Campaign ID</p>
                <code className="block rounded-md bg-muted px-2 py-1.5 font-mono text-xs break-all" data-testid="webhooks-campaign-id">
                  {webhooksCamp.id}
                </code>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Endpoint</p>
                <code className="block rounded-md bg-muted px-2 py-1.5 font-mono text-xs break-all">
                  {`POST /api/campaigns/${webhooksCamp.id}/webhook/dial`}
                </code>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Example body</p>
                <pre className="rounded-md bg-muted px-2 py-1.5 font-mono text-[11px] overflow-x-auto whitespace-pre-wrap">{`{
  "phone": "+15551234567",
  "lead_id": "optional-lead-id",
  "fields": {}
}`}</pre>
              </div>
              <p className="text-xs text-muted-foreground">
                Authenticate with your customer API token. Working days and dialing windows do not block webhook hits while the campaign is active.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="rounded-lg"
              onClick={() => {
                if (!webhooksCamp?.id) return;
                navigator.clipboard.writeText(webhooksCamp.id).then(
                  () => toast.success("Campaign ID copied"),
                  () => toast.error("Copy failed"),
                );
              }}
              data-testid="webhooks-copy-id"
            >
              Copy campaign ID
            </Button>
            <Button type="button" className="rounded-lg" onClick={() => setWebhooksCamp(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {asterisk && (
        <AsteriskWizard campaign={asterisk} open={!!asterisk} onClose={() => setAsterisk(null)} />
      )}

      <RichHtmlEditorDialog
        open={!!scriptCamp}
        onOpenChange={(v) => { if (!v && !savingScript) setScriptCamp(null); }}
        title={scriptCamp ? `Agent script — ${scriptCamp.name}` : "Agent script"}
        description="Rich HTML shown under the Script tab on the agent screen. Paste keeps bold, headings, and bullets."
        value={scriptCamp?.agent_script || ""}
        maxLength={50000}
        placeholder="Write the script agents should follow…"
        onSave={saveAgentScript}
        testid="campaign-agent-script-dialog"
      />

      {/* Call limit = 0 — modal with OK → Call Limit page */}
      <Dialog open={!!callLimitModal} onOpenChange={(v) => !v && setCallLimitModal(null)}>
        <DialogContent data-testid="call-limit-zero-dialog">
          <DialogHeader>
            <DialogTitle>Call limit required</DialogTitle>
            <DialogDescription>{callLimitModal}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="rounded-lg"
              data-testid="call-limit-zero-ok"
              onClick={() => {
                setCallLimitModal(null);
                navigate("/call-limit");
              }}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* VICI Bot requires at least one active VICIdial server */}
      <Dialog open={noVicidialOpen} onOpenChange={setNoVicidialOpen}>
        <DialogContent data-testid="no-vicidial-dialog">
          <DialogHeader>
            <DialogTitle>VICIdial server required</DialogTitle>
            <DialogDescription>
              You don&apos;t have any active VICIdial server. Add a VICIdial server before creating a VICI Bot campaign.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              className="rounded-lg"
              data-testid="no-vicidial-ok"
              onClick={() => {
                setNoVicidialOpen(false);
                navigate("/servers/vicidial");
              }}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy / Delete confirmation */}
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid="campaign-confirm-dialog">
          <DialogHeader>
            <DialogTitle>
              {confirm?.action === "delete" ? "Delete campaign?" : confirm?.action === "archive" ? "Archive campaign?" : "Copy campaign?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.action === "delete"
                ? `This deletes "${confirm?.camp?.name}", stops it and releases any active holds. This cannot be undone.`
                : confirm?.action === "archive"
                ? `This archives "${confirm?.camp?.name}" and stops it. You can restore it anytime from "Show archived".`
                : `This creates a private copy of "${confirm?.camp?.name}" (flow and settings included).`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirm(null)} data-testid="campaign-confirm-cancel">Cancel</Button>
            <Button
              className={`rounded-lg ${confirm?.action === "delete" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}`}
              onClick={runConfirm} data-testid="campaign-confirm-ok">
              {confirm?.action === "delete" ? "Delete campaign" : confirm?.action === "archive" ? "Archive campaign" : "Copy campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}
